import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import PacientesPage from '@/pages/PacientesPage';
import AgendaPage from '@/pages/AgendaPage';
import NovaConsultaPage from '@/pages/NovaConsultaPage';
import ProntuarioPage from '@/pages/ProntuarioPage';
import ConfiguracoesPage from '@/pages/ConfiguracoesPage';
import PainelMasterPage from '@/pages/PainelMasterPage';
import ConsultaDetalhesPage from '@/pages/ConsultaDetalhesPage';
import ControleRetornoPage from '@/pages/ControleRetornoPage';
import ConfiguracoesUsuarioPage from '@/pages/ConfiguracoesUsuarioPage';
import CardMockupsPage from '@/pages/CardMockupsPage';
import CancelamentoMockupsPage from '@/pages/CancelamentoMockupsPage';
import AppMockupsPage from '@/pages/AppMockupsPage';
import AppMockupsV2Page from '@/pages/AppMockupsV2Page';
import AppMockupsRadixPage from '@/pages/AppMockupsRadixPage';
import AppMockupsRadixV2Page from '@/pages/AppMockupsRadixV2Page';
import PendenciasMockupsPage from '@/pages/PendenciasMockupsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor para adicionar token - exceto rotas do painel master
axios.interceptors.request.use(
  (config) => {
    const isMasterRoute = config.url?.includes('/master/');
    if (!isMasterRoute) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios interceptor para tratar erros - não redirecionar no painel master
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!window.location.pathname.startsWith('/painel/master')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const savedShowConfig = localStorage.getItem('showConfig');
    
    if (token && userData) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
        setShowConfig(savedShowConfig === 'true');
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('showConfig');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData, canShowConfig = false) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('showConfig', canShowConfig.toString());
    setUser(userData);
    setShowConfig(canShowConfig);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('showConfig');
    setUser(null);
    setShowConfig(false);
    setIsAuthenticated(false);
    toast.success('Logout realizado com sucesso!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Painel Master - Rota separada */}
          <Route path="/painel/master" element={<PainelMasterPage />} />

          {/* Mockups públicos - card variations preview */}
          <Route path="/mockups/cards" element={<CardMockupsPage />} />
          <Route path="/mockups/cancelamento" element={<CancelamentoMockupsPage />} />
          <Route path="/mockups/app" element={<AppMockupsPage />} />
          <Route path="/mockups/app-v2" element={<AppMockupsV2Page />} />
          <Route path="/mockups/radix" element={<AppMockupsRadixPage />} />
          <Route path="/mockups/radix-v2" element={<AppMockupsRadixV2Page />} />
          <Route path="/mockups/pendencias" element={<PendenciasMockupsPage />} />
          
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <DashboardPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/pacientes"
            element={
              isAuthenticated ? (
                <PacientesPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/agenda"
            element={
              isAuthenticated ? (
                <AgendaPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/consulta/:id"
            element={
              isAuthenticated ? (
                <ConsultaDetalhesPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/controle-retorno"
            element={
              isAuthenticated ? (
                <ControleRetornoPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/nova-consulta"
            element={
              isAuthenticated ? (
                <NovaConsultaPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/pacientes/prontuario/:pacienteId"
            element={
              isAuthenticated ? (
                <ProntuarioPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/configuracoes"
            element={
              isAuthenticated && showConfig ? (
                <ConfiguracoesPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/configuracoes-usuario"
            element={
              isAuthenticated ? (
                <ConfiguracoesUsuarioPage user={user} onLogout={handleLogout} showConfig={showConfig} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
